// When using this component, make sure to set a unique html element ID because the watch method depends on ID
<template>
  <textarea v-bind:value="value"></textarea>
</template>

<script>
  /* eslint-disable */

  import { mapState, mapActions } from 'vuex'

  export default {
    name: 'Tinymce',

    props: ['value', 'disabled', 'imagePath', 'height', 'containerName'],
    methods: {
      ...mapActions('globals/', ['POST_BLOB']),
      focus() {
        tinymce.get(this.$el.id).focus()
      },
      updateValue: function (value) {
        this.$emit('input', value.trim())
        this.$emit('change')
      },
      handleDisabledProp: function (disabled) {
        try {
          const mceInstance = tinymce.get(this.$el.id)
          if (mceInstance) {
            // set the body to disabled and hide the toolbar buttons
            if (disabled) {
              mceInstance.mode.set('readonly')
              // otherwise set the body to enabled and show the toolbar buttons
            } else {
              mceInstance.mode.set('design')
            }
          }
        } catch (e) {
          console.log('Error setting disabled prop for Tinymce', e)
        }
      },
      initialize() {
        var component = this
        const height = this.height ? this.height : '500'
        tinymce.init({
          relative_urls: false,
          convert_urls: false,
          skin_url: '/static/skins/ui/oxide',
          menubar: false,
          target: this.$el,
          height: height,
          plugins: 'preview lists tinymcespellchecker powerpaste',
          forced_root_block: '', // forces single line after enter key
          powerpaste_allow_local_images: true,
          powerpaste_word_import: 'clean',
          powerpaste_html_import: 'clean',
          browser_spellcheck: true,
          spellchecker_dialog: true,
          spellchecker_languages: 'US English=en',
          spellchecker_active: false,
          spellchecker_on_load: true,
          spellchecker_ignore_list: this.spellCheckDictionary,
          spellchecker_rpc_url: 'https://tiny-spellcheck-linux.azurewebsites.net/',
          paste_as_text: false,
          fontsize_formats: '8pt 9pt 10pt 11pt 12pt 14pt 18pt 24pt 36pt',
          convert_fonts_to_spans: false,
          toolbar_items_size: 'small',
          toolbar: 'spellchecker undo redo | forecolor backcolor | bold italic underline strikethrough | fontsize | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | preview image code',
          images_upload_handler: async blobInfo => {
            const payload = {
              blobPathPrefix: this.imagePath ? this.imagePath : 'misc/',
              fileExtension: 'png',
              blob: blobInfo.blob(),
              containerName: this.containerName ? this.containerName : null
            }

            return this.POST_BLOB(payload)
              .then(response => {
                // Return the image URL to TinyMCE
                return response.data.location
              })
              .then(url => {
                // Delay just a tick to let TinyMCE insert the image
                // and then emit the update event to the parent
                setTimeout(() => {
                  let mceInstance = tinymce.get(this.$el.id)
                  this.updateValue(mceInstance.getContent())
                }, 0)
                return url
              })
              .catch(error => {
                return Promise.reject({
                  message: 'Failed to upload image',
                  error: error,
                  remove: true
                })
              })
          },
          paste_preprocess: (plugin, source) => {
            source.content = source.content
              .replace(/<h1.*?>/g, '<p style="font-weight: bold; font-size: 26px;">')
              .replace(/<h2.*?>/g, '<p style="font-weight: bold; font-size: 20px;">')
              .replace(/<h3.*?>/g, '<p style="font-weight: bold; font-size: 16px;">')
              .replace(/<h4.*?>/g, '<p style="font-weight: bold; font-size: 14px;">')
              .replace(/<h5.*?>/g, '<p style="font-weight: bold; font-size: 14px;">')
              .replace(/<h6.*?>/g, '<p style="font-weight: bold; font-size: 14px;">')
              .replace(/<\/h.+?>/g, '</p>')
              // replace table border and padding 0 so that tables will render in Aspose reports
              .replace(/border="0"/g, 'border="1"')
              .replace(/cellpadding="0"/g, 'cellpadding="2"')
          },
          setup: editor => {
            editor.on('blur', e => {
              if (!e.target.isNotDirty && this.value !== editor.getContent()) {
                component.updateValue(editor.getContent())
              }
            })
            editor.on('init', e => {
              this.handleDisabledProp(this.disabled)
            })
            editor.on('dirty', e => {
              this.$emit('dirty')
            })
          },
          init_instance_callback: editor => {
            // after the v7 upgrade, the HTML 5 spellcheck was set to false
            // preventing chrome's naitve red-underline spell checking. This reenables it.
            editor.getBody().setAttribute('spellcheck', true)
          }
        })
      }
    },
    mounted: function () {
      this.initialize()
    },
    computed: {
      ...mapState('globals/', ['spellCheckDictionary'])
    },
    watch: {
      value() {
        if (this.value && this.value !== tinymce.get(this.$el.id).getContent()) {
          tinymce.get(this.$el.id).setContent(this.value)
        } else if (!this.value) {
          // tinymce yells if you give it a null, replace with emty string
          tinymce.get(this.$el.id).setContent('')
        }
      },
      disabled() {
        this.handleDisabledProp(this.disabled)
      }
    },
    beforeDestroy: function () {
      // if value exists update when navigating away using browser back button
      if (tinymce.get(this.$el.id) !== null) {
        if (!tinymce.get(this.$el.id).isNotDirty && this.value !== tinymce.get(this.$el.id).getContent()) {
          this.updateValue(tinymce.get(this.$el.id).getContent())
        }
      }

      tinymce.get(this.$el.id).destroy()
    },
    deactivated: function () {
      // doing this solves the TinyMCE not loading properly during navigation
      // this is due to keep alive; exluding does not work the behavior varies
      // works for now until better logic.
      this.$destroy()
    }
  }
</script>

<style>
.tox-statusbar__branding {
  display: none;
}

.tox-tinymce {
  border-radius: 5px !important;
  border: 1px solid #dcdfe6 !important;
}

.tox-tbtn:focus {
  box-shadow: none !important;
}

button[data-mce-name='fontsize']:focus::after {
  box-shadow: none !important;
}
</style>
